import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  LinearProgress,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import * as XLSX from "xlsx";
import { getSubOrdersForReport } from "../../../services/SubOrder";

const statusOptions = [
  { value: "DRAFT", label: "Draft" },
  { value: "AWAITING_APPROVAL", label: "Awaiting Approval" },
  { value: "REJECTED", label: "Rejected" },
  { value: "AWAITING_FOR_SUPPLY_ITEMS", label: "Awaiting For Supply Items" },
  { value: "PROCESSING", label: "Order Processing" },
  { value: "AWAITING_CONFIRMATION", label: "Awaiting Confirmation" },
  { value: "COMPLETED", label: "Completed" },
];

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(2.5, 3),
}));

const SummaryBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.success.main, 0.06),
  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
  textAlign: "center",
}));

function SubOrderReportDialog({ open, onClose }) {
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const getStatusLabel = (status) =>
    statusOptions.find((opt) => opt.value === status)?.label || status || "";

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    setPreview(null);
    setProgress(0);

    try {
      const startStr = startDate ? startDate.format("YYYY-MM-DD") : null;
      const endStr = endDate ? endDate.format("YYYY-MM-DD") : null;

      setProgressLabel("Fetching sub orders...");
      setProgress(20);

      const raw = await getSubOrdersForReport(startStr, endStr);

      // Handle both parsed object and JSON string responses
      let result = raw;
      if (typeof raw === "string") {
        try {
          result = JSON.parse(raw);
        } catch (e) {
          result = null;
        }
      }

      if (!result) {
        setError("Failed to fetch orders — no response from server.");
        setLoading(false);
        return;
      }

      // Support both {content:[]} and plain array responses
      let orders = Array.isArray(result) ? result : result.content;
      if (!Array.isArray(orders)) {
        setError(`Unexpected response format. Please try again.`);
        setLoading(false);
        return;
      }

      // Date filter — handle "2026-04-24 07:11:38" (space) and ISO "T" formats
      if (startStr || endStr) {
        orders = orders.filter((order) => {
          const rawDate = order.orderDate || order.date || order.createdDate || "";
          const dateOnly = rawDate.split(/[ T]/)[0]; // works for both space and T
          if (!dateOnly) return true;
          if (startStr && dateOnly < startStr) return false;
          if (endStr && dateOnly > endStr) return false;
          return true;
        });
      }

      if (orders.length === 0) {
        setError("No sub orders found for the selected date range.");
        setLoading(false);
        return;
      }

      setProgressLabel(`Building report for ${orders.length} orders...`);
      setProgress(60);

      // All needed data is already in the list response — no extra API calls needed
      const rows = [];

      orders.forEach((order) => {
        const rawDate = order.orderDate || order.date || order.createdDate || "";
        const orderDate = rawDate.split(/[ T]/)[0];
        const orderId = order.orderId;
        const personName = order.subOrderPerson?.personName || "";
        const status = getStatusLabel(order.status);
        const totalCost = order.cost || 0;
        const paidAmount = order.paidAmount || 0;
        const remainingCost = Math.max(0, totalCost - paidAmount);

        const outputItems = (order.lineItems || []).filter(
          (li) => li.lineItemType === "OUTPUT"
        );

        if (outputItems.length === 0) {
          rows.push({
            Date: orderDate,
            "Order Number": orderId,
            "Order Person": personName,
            "Output Product Code": "",
            "Output Product Name": "",
            Status: status,
            "Received Qty": 0,
            "Damaged Qty": 0,
            "Total Cost": totalCost,
            "Paid Amount": paidAmount,
            "Remaining Cost": remainingCost,
          });
        } else {
          outputItems.forEach((item) => {
            rows.push({
              Date: orderDate,
              "Order Number": orderId,
              "Order Person": personName,
              "Output Product Code": item.imports?.product_code || "",
              "Output Product Name": item.imports?.itemName || "",
              Status: status,
              "Received Qty": item.totalReceivedQty || 0,
              "Damaged Qty": item.totalDamagedQty || 0,
              "Total Cost": totalCost,
              "Paid Amount": paidAmount,
              "Remaining Cost": remainingCost,
            });
          });
        }
      });

      console.log("[Report] Total Excel rows:", rows.length);
      if (rows.length === 0) {
        setError(`No data to export. Orders found: ${orders.length}, but no row data could be built. Check browser console for details.`);
        setLoading(false);
        return;
      }

      setProgress(85);
      setProgressLabel("Generating Excel file...");

      // Build workbook
      const ws = XLSX.utils.json_to_sheet(rows);

      // Column widths
      const colKeys = Object.keys(rows[0]);
      ws["!cols"] = colKeys.map((key) => ({
        wch: Math.max(
          key.length + 2,
          ...rows.map((r) => String(r[key] ?? "").length + 1)
        ),
      }));

      // Header row styling (bold via cell formats)
      const headerRange = XLSX.utils.decode_range(ws["!ref"]);
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddr = XLSX.utils.encode_cell({ r: 0, c: col });
        if (ws[cellAddr]) {
          ws[cellAddr].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "1976D2" } },
            alignment: { horizontal: "center" },
          };
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sub Orders Summary");

      const fileName = `SubOrder_Report_${startStr || "all"}_to_${endStr || "all"}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setProgress(100);
      setPreview({ totalOrders: orders.length, totalRows: rows.length, fileName });
    } catch (err) {
      console.error("Report generation error:", err);
      setError("An error occurred while generating the report.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setPreview(null);
      setProgress(0);
      onClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <Box display="flex" alignItems="center" gap={1.5}>
            <AssessmentIcon fontSize="large" />
            <Box>
              <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                Sub Order Summary Report
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Export to Excel
              </Typography>
            </Box>
          </Box>
        </StyledDialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Select a date range to generate an Excel summary report. The report
            includes Date, Order Number, Order Person, Output Products, Status,
            Received &amp; Damaged Quantities, and Payment details.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {preview && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<FileDownloadIcon />}>
              <Typography variant="body2" fontWeight="bold">
                Report downloaded: {preview.fileName}
              </Typography>
              <Typography variant="caption">
                {preview.totalOrders} orders · {preview.totalRows} rows exported
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              border: (theme) =>
                `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CalendarTodayIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="bold" color="primary">
                Select Date Range
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {loading && (
            <Box mt={2}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {progressLabel}
                </Typography>
                <Typography variant="caption" color="primary">
                  {progress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          )}

          <Divider sx={{ mt: 2 }} />

          <Box mt={2}>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
            >
              <strong>Report columns:</strong> Date · Order Number · Order Person
              · Output Product Code · Output Product Name · Status · Received Qty
              · Damaged Qty · Total Cost · Paid Amount · Remaining Cost
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={generateReport}
            disabled={loading || !startDate || !endDate}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FileDownloadIcon />
              )
            }
            sx={{ minWidth: 170, fontWeight: 600 }}
          >
            {loading ? "Generating..." : "Generate & Download"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default SubOrderReportDialog;
